/**
 * Textes de l'écran « Coffre des secrets » (ADR 0095, 2026-10-27), langue de référence. Branché sous la
 * clé `secretStore` de `fr.ts` ; `secret-store.en.ts` porte exactement les mêmes clés.
 */
export const secretStoreFr = {
  title: 'Coffre des secrets',
  subtitle:
    'Où vivent les valeurs des secrets de l’organisation : chiffrées en base par défaut, ou dans le coffre que vous choisissez — Azure Key Vault, HashiCorp Vault ou AWS Secrets Manager.',
  providers: {
    database: 'Coffre chiffré en base (défaut)',
    'azure-key-vault': 'Azure Key Vault',
    'hashicorp-vault': 'HashiCorp Vault (KV v2)',
    'aws-secrets-manager': 'AWS Secrets Manager',
  },
  writes: {
    external: 'Dans le coffre externe',
    database: 'En base, chiffrées',
  },
  auths: {
    'client-secret': 'Principal de service (secret client)',
    'managed-identity': 'Identité managée de la plateforme',
    token: 'Jeton',
    approle: 'AppRole',
    'access-key': 'Clé d’accès',
    'default-chain': 'Identité de la plateforme (chaîne par défaut)',
  },
  keys: {
    vault_url: 'URL du coffre',
    tenant_id: 'Annuaire (tenant ID)',
    client_id: 'Application (client ID)',
    client_secret: 'Secret client',
    name_prefix: 'Préfixe des noms',
    address: 'Adresse du serveur Vault',
    mount: 'Montage KV v2',
    path_prefix: 'Préfixe des chemins',
    namespace: 'Namespace (Vault Enterprise)',
    approle_mount: 'Montage AppRole',
    role_id: 'Role ID',
    token: 'Jeton Vault',
    secret_id: 'Secret ID',
    region: 'Région',
    access_key_id: 'Access key ID',
    secret_access_key: 'Secret access key',
  },
  summary: {
    title: 'État actuel',
    provider: 'Coffre',
    writes: 'Nouvelles valeurs',
    inDatabase: 'Valeurs en base',
    inExternal: 'Valeurs dans le coffre externe',
    parameters: 'Paramètres de connexion',
    credentials: 'Identifiants posés',
    noCredentials: 'Aucun (identité de la plateforme)',
    credentialSet: 'posé — jamais réaffiché',
    readsByForm:
      'Une valeur se lit toujours là où elle vit : changer le réglage des écritures ne déplace rien. Pour déplacer les valeurs existantes, utilisez la migration.',
  },
  check: {
    action: 'Vérifier la connexion',
    ok: 'Le coffre répond : une valeur témoin a été écrite puis relue ({{duration}} ms).',
    failed: 'Le coffre ne répond pas comme attendu.',
  },
  migration: {
    toExternal_one: 'Déplacer {{count}} valeur vers le coffre externe',
    toExternal_other: 'Déplacer {{count}} valeurs vers le coffre externe',
    toDatabase_one: 'Ramener {{count}} valeur en base',
    toDatabase_other: 'Ramener {{count}} valeurs en base',
    title: {
      external: 'Migration vers le coffre externe',
      database: 'Retour en base',
    },
    counts:
      '{{moved}} déplacée(s), {{skipped}} ignorée(s) (déjà en place ou modifiées entre-temps), {{failed}} en échec.',
    done: {
      external: 'Migration vers le coffre externe terminée.',
      database: 'Retour en base terminé.',
    },
  },
  remove: {
    action: 'Retirer le coffre externe',
    title: 'Retirer le coffre externe ?',
    confirm:
      'L’organisation revient au coffre chiffré en base et ses identifiants de connexion sont oubliés. Refusé tant qu’une valeur vit encore dans le coffre externe.',
    done: 'Coffre externe retiré : les secrets sont de nouveau chiffrés en base.',
    inUse_one:
      '{{count}} valeur vit dans le coffre externe : ramenez-la en base avant de retirer le coffre ou d’en changer.',
    inUse_other:
      '{{count}} valeurs vivent dans le coffre externe : ramenez-les en base avant de retirer le coffre ou d’en changer.',
  },
  form: {
    createTitle: 'Désigner un coffre externe',
    editTitle: 'Modifier le coffre externe',
    help: 'La connexion est vérifiée avant d’être enregistrée : un coffre qui ne répond pas n’est jamais enregistré. Les identifiants sont scellés en base et ne sont jamais réaffichés.',
    submit: 'Vérifier et enregistrer',
    saved: 'Coffre vérifié et enregistré.',
    noProvider: 'Aucun coffre externe n’est autorisé sur cette plateforme.',
  },
  fields: {
    provider: 'Fournisseur',
    providerHelp: 'Seuls les fournisseurs autorisés par l’exploitant de la plateforme sont proposés.',
    writes: 'Écrire les nouvelles valeurs',
    writesHelp:
      '« En base » arrête les écritures dans le coffre externe sans rien déplacer : étape du retour en base.',
    auth: 'Authentification',
    credentialHelp: 'Saisi une fois, scellé en base, jamais réaffiché.',
    credentialKept: 'Déjà posé : laissez vide pour le garder, saisissez une valeur pour le remplacer.',
  },
  codes: {
    SECRET_STORE_INVALID: 'Paramètres du coffre invalides : vérifiez l’URL, l’adresse ou la région.',
    SECRET_STORE_IN_USE:
      'Des valeurs vivent encore dans ce coffre : ramenez-les en base avant de le retirer, de changer de fournisseur ou de coffre.',
    SECRET_STORE_NOT_CONFIGURED: 'Aucun coffre externe n’est désigné pour cette organisation.',
    SECRET_STORE_NOT_FOUND: 'Aucun coffre externe n’est désigné pour cette organisation.',
    SECRET_STORE_UNAVAILABLE:
      'Le coffre externe est injoignable (réseau, délai dépassé ou erreur du service) : réessayez plus tard ou vérifiez que la plateforme peut le joindre.',
    SECRET_STORE_DENIED:
      'Le coffre externe refuse l’accès : identifiants invalides ou droits manquants (lire, écrire et supprimer des secrets).',
    SECRET_STORE_VALUE_MISSING:
      'Une valeur référencée n’existe plus dans le coffre externe : ressaisissez le secret.',
    feature_disabled:
      'Ce fournisseur, ou l’identité de la plateforme, n’est pas autorisé par l’exploitant de cette installation.',
  },
};
